/**
 * Coupon Discount Pricing Module
 *
 * Uses Storefront API shadow cart to determine per-variant eligibility,
 * compares native sale discounts against coupon discounts,
 * and displays strikethrough pricing for the better discount.
 *
 * Flow:
 * 1. Capture utm_source from URL or localStorage
 * 2. Collect variant IDs from PLP/PDP DOM
 * 3. Create shadow cart via Storefront API cartCreate mutation
 * 4. Parse discountAllocations per variant
 * 5. Cache eligibility results with 5-min TTL
 * 6. Compare native sale vs coupon discount and display the better one
 */

(function() {
  'use strict';

  // ─── Constants ───────────────────────────────────────────────

  var CACHE_KEY_PREFIX = 'coupon-discounts-v2:';
  var CACHE_TTL = 5 * 60 * 1000; // 5 minutes; discount rules can change during campaigns
  var API_VERSION = '2025-01';
  var BATCH_SIZE = 250; // Storefront API max lines per cart
  var FETCH_TIMEOUT = 5000; // 5 seconds
  var HOVER_PREFETCH_DELAY = 400; // ms
  var MUTATION_DEBOUNCE = 300; // ms
  var MAX_RETRIES = 3;
  var UTM_SOURCE_STORAGE_KEY = 'coupon-discount-utm-source';
  var CART_SYNC_TIMEOUT = 5000; // 5 seconds for real cart API calls
  var CART_SYNC_DEBOUNCE = 250; // ms debounce for cart sync triggers

  // ─── State ────────────────────────────────────────────────────

  var queriedVariantIds = {}; // IDs already queried in this session
  var pendingMutationIds = []; // IDs from MutationObserver
  var debounceTimer = null;
  var hoverPrefetchQueue = [];
  var hoverPrefetchTimer = null;
  var storageRevalidationTimer = null;
  var pdpFallbackTimer = null;
  var discountCode = null; // Current active discount code
  var initialUrlUtmSourceCaptured = false;
  var formatMoneyCache = {}; // Cache for money formatting

  // ─── Real Cart Discount Sync State ───────────────────────────

  /**
   * Cart discount sync state management.
   * Separate from visual pricing - this handles applying discount codes
   * to the real Shopify cart via Ajax API.
   * State is keyed by cart.token + discount code to prevent duplicate work.
   */
  var cartDiscountSync = {
    status: 'idle', // idle | checking | applying | applied | retryable | failed
    inFlight: false,
    appliedKey: null, // Format: cartToken + '|' + discountCode
    retryAttempts: {}, // Format: cartToken + '|' + discountCode + '|' + reason + '|' + cart signature
    lastRetryKey: null,
    debounceTimer: null
  };

  function cartHasDiscountCode(cart, code) {
    if (!cart || !code) return false;

    var discountApplications = cart.cart_level_discount_applications || cart.discount_applications || [];

    for (var i = 0; i < discountApplications.length; i++) {
      var app = discountApplications[i];
      if (app.title === code || (app.code && app.code === code)) {
        return true;
      }
    }

    if (!cart.items) return false;

    for (var j = 0; j < cart.items.length; j++) {
      var item = cart.items[j];
      var lineDiscounts = item.discounts || item.line_level_discount_allocations || [];

      for (var k = 0; k < lineDiscounts.length; k++) {
        var lineDiscount = lineDiscounts[k];

        if (lineDiscount.discount_application) {
          var lineApp = lineDiscount.discount_application;
          if (lineApp.title === code || (lineApp.code && lineApp.code === code)) {
            return true;
          }
        }

        if (lineDiscount.code === code || lineDiscount.title === code) {
          return true;
        }
      }
    }

    return false;
  }

  function getCartItemSignature(cart) {
    if (!cart || !cart.items || !cart.items.length) {
      return String((cart && cart.item_count) || 0);
    }

    var parts = [];

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i] || {};
      var itemId = item.key || item.variant_id || item.id || i;
      var quantity = typeof item.quantity === 'number' ? item.quantity : 0;
      parts.push(String(itemId) + ':' + String(quantity));
    }

    parts.sort();
    return String(cart.item_count || 0) + ':' + parts.join(',');
  }

  function getCartDiscountRetryKey(cart, code, reason) {
    return [
      cart.token,
      code,
      reason,
      getCartItemSignature(cart)
    ].join('|');
  }

  function hasCartDiscountRetryAttempt(retryKey) {
    return !!cartDiscountSync.retryAttempts[retryKey];
  }

  function markCartDiscountRetryAttempt(retryKey) {
    cartDiscountSync.retryAttempts[retryKey] = true;
    cartDiscountSync.lastRetryKey = retryKey;
  }

  function normalizeUtmSource(value) {
    if (value === null || typeof value === 'undefined') return null;

    var normalized = String(value).trim();
    return normalized || null;
  }

  function storeUtmSource(value) {
    var normalized = normalizeUtmSource(value);
    if (!normalized) return null;

    try {
      if (localStorage.getItem(UTM_SOURCE_STORAGE_KEY) !== normalized) {
        localStorage.setItem(UTM_SOURCE_STORAGE_KEY, normalized);
      }
    } catch (e) {
      // localStorage unavailable (private mode, quota exceeded)
    }

    return normalized;
  }

  function getUrlUtmSource() {
    try {
      var params = new URLSearchParams(window.location.search);
      return normalizeUtmSource(params.get('utm_source'));
    } catch (e) {
      return null;
    }
  }

  function getStoredUtmSource() {
    try {
      return normalizeUtmSource(localStorage.getItem(UTM_SOURCE_STORAGE_KEY));
    } catch (e) {
      return null;
    }
  }

  /**
   * Clears coupon cache entries from localStorage.
   * Does NOT clear coupon-discount-utm-source (campaign persists per design).
   * Only clears cache keys with prefix 'coupon-discounts-v2:'.
   */
  function clearCouponCache() {
    try {
      var keys = Object.keys(localStorage);
      for (var i = 0; i < keys.length; i++) {
        if (keys[i].indexOf(CACHE_KEY_PREFIX) === 0) {
          localStorage.removeItem(keys[i]);
        }
      }
    } catch (e) {
      // localStorage unavailable
    }
  }

  // ─── Deprecated: clearDiscountSession() removed in PR 2 ───
  // The old clearDiscountSession() cleared coupon-discount-utm-source, which
  // violates the design requirement that stored campaign UTM persists across
  // sessions. Use clearCouponCache() if you need to invalidate cached pricing.

  // ─── Real Cart Discount Sync ────────────────────────────────

  /**
   * Applies stored UTM discount code to the real Shopify cart.
   * Idempotent: checks cart state first, only applies if needed.
   * Separate from visual pricing - does not affect PLP/PDP shadow cart simulation.
   * 
   * @param {Object} options - Sync options
   * @param {string} options.reason - Trigger reason: 'cart-update' | 'cart-page' | 'checkout' | 'utm-change'
   * @returns {Promise<void>} - Resolves when sync complete or skipped
   */
  function ensureCartDiscountApplied(options) {
    options = options || {};
    var reason = options.reason || 'unknown';

    // Read stored UTM - exit early if no campaign active
    var code = getStoredUtmSource();
    if (!code) {
      return Promise.resolve();
    }

    // In-flight lock: prevent duplicate concurrent syncs
    if (cartDiscountSync.inFlight) {
      return Promise.resolve();
    }

    // Check existing appliedKey for code mismatch (different campaign)
    // Do NOT return early based on code-only comparison - we must GET cart
    // to discover whether the token matches (cart may have been recreated)
    var currentAppliedKey = cartDiscountSync.appliedKey;
    var needsReset = false;
    
    if (currentAppliedKey) {
      var parts = currentAppliedKey.split('|');
      var appliedCode = parts[1] || '';
      
      // Different code: reset sync state for new campaign (but still fetch to discover token)
      if (appliedCode !== code) {
        needsReset = true;
      }
      // Same code: cannot skip without knowing token - proceed to GET
    }

    // Reset state if different campaign
    if (needsReset) {
      resetCartDiscountSyncState();
    }

    // Mark as checking and set in-flight
    cartDiscountSync.status = 'checking';
    cartDiscountSync.inFlight = true;

    // Create AbortController for timeout
    var controller = new AbortController();
    var timeoutId = setTimeout(function() {
      controller.abort();
    }, CART_SYNC_TIMEOUT);

    // Store current appliedKey for post-GET comparison
    var preFetchAppliedKey = cartDiscountSync.appliedKey;

    // Step 1: GET /cart.js to check current state
    return fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      signal: controller.signal
    })
    .then(function(response) {
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        cartDiscountSync.status = 'failed';
        cartDiscountSync.inFlight = false;
        console.warn('[CartDiscountSync] GET /cart.js failed:', response.status);
        return null;
      }
      
      return response.json();
    })
    .catch(function(error) {
      clearTimeout(timeoutId);
      cartDiscountSync.status = 'failed';
      cartDiscountSync.inFlight = false;
      
      if (error.name !== 'AbortError') {
        console.warn('[CartDiscountSync] GET /cart.js error:', error);
      }
      return null;
    })
    .then(function(cart) {
      if (!cart) {
        // Network error or timeout - preserve native pricing, no UTM clearing
        return;
      }

      // Empty cart or no token: skip sync, don't mutate
      if (!cart.token || cart.item_count === 0) {
        cartDiscountSync.status = 'idle';
        cartDiscountSync.inFlight = false;
        return;
      }

      // Compare full appliedKey (token + code) after discovering cart token
      // Only skip if SAME token AND SAME code (proven applied pair)
      if (preFetchAppliedKey) {
        var candidateKey = cart.token + '|' + code;
        if (preFetchAppliedKey === candidateKey) {
          // Same cart token + same code: already synced for this pair
          cartDiscountSync.status = 'applied';
          cartDiscountSync.inFlight = false;
          return;
        }
        // Different token: proceed with sync (cart recreated or new session)
      }

      // Check cart-level and line item discounts for the stored code.
      var alreadyApplied = cartHasDiscountCode(cart, code);

      if (alreadyApplied) {
        // Code already applied: mark success, no further sync needed
        var appliedKeyNew = cart.token + '|' + code;
        cartDiscountSync.appliedKey = appliedKeyNew;
        cartDiscountSync.status = 'applied';
        cartDiscountSync.inFlight = false;
        return;
      }

      var retryKey = getCartDiscountRetryKey(cart, code, reason);

      if (hasCartDiscountRetryAttempt(retryKey)) {
        // This cart/code/trigger/material state already failed to confirm.
        // Keep the UTM and wait for a later material trigger with a different key.
        cartDiscountSync.status = 'retryable';
        cartDiscountSync.inFlight = false;
        return;
      }

      // Code not applied: proceed with POST to apply
      cartDiscountSync.status = 'applying';
      markCartDiscountRetryAttempt(retryKey);

      // Create new AbortController for POST
      var postController = new AbortController();
      var postTimeoutId = setTimeout(function() {
        postController.abort();
      }, CART_SYNC_TIMEOUT);

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ discount: code }),
        signal: postController.signal
      })
      .then(function(postResponse) {
        clearTimeout(postTimeoutId);
        
        if (!postResponse.ok) {
          cartDiscountSync.status = 'failed';
          cartDiscountSync.inFlight = false;
          console.warn('[CartDiscountSync] POST /cart/update.js failed:', postResponse.status);
          return;
        }

        return postResponse.json()
          .catch(function() {
            return null;
          })
          .then(function(updatedCart) {
            var confirmedToken = updatedCart && updatedCart.token ? updatedCart.token : cart.token;

            if (updatedCart && cartHasDiscountCode(updatedCart, code)) {
              // Success only after Shopify confirms the code is present.
              cartDiscountSync.appliedKey = confirmedToken + '|' + code;
              cartDiscountSync.status = 'applied';
              cartDiscountSync.inFlight = false;
              return;
            }

            cartDiscountSync.status = 'retryable';
            cartDiscountSync.inFlight = false;
            console.warn('[CartDiscountSync] POST /cart/update.js did not confirm discount code:', code);
          });
      })
      .catch(function(postError) {
        clearTimeout(postTimeoutId);
        cartDiscountSync.status = 'failed';
        cartDiscountSync.inFlight = false;
        
        // Do NOT clear stored UTM on failure
        // Retry only on later material trigger
        if (postError.name !== 'AbortError') {
          console.warn('[CartDiscountSync] POST /cart/update.js error:', postError);
        }
      });
    });
  }

  /**
   * Resets cart discount sync state (e.g., on cart token change or UTM change).
   * Does NOT clear stored UTM - only resets sync tracking state.
   */
  function resetCartDiscountSyncState() {
    cartDiscountSync.status = 'idle';
    cartDiscountSync.inFlight = false;
    cartDiscountSync.appliedKey = null;
    cartDiscountSync.retryAttempts = {};
    cartDiscountSync.lastRetryKey = null;
    
    if (cartDiscountSync.debounceTimer) {
      clearTimeout(cartDiscountSync.debounceTimer);
      cartDiscountSync.debounceTimer = null;
    }
  }

  function captureInitialUrlUtmSource() {
    if (initialUrlUtmSourceCaptured) {
      return getStoredUtmSource();
    }

    initialUrlUtmSourceCaptured = true;

    var utmSource = getUrlUtmSource();

    if (utmSource) {
      return storeUtmSource(utmSource);
    }

    return getStoredUtmSource();
  }

  function setActiveDiscountCode(code) {
    var nextCode = normalizeUtmSource(code);

    if (discountCode !== nextCode) {
      queriedVariantIds = {};
    }

    discountCode = nextCode;
    return discountCode;
  }

  // ─── Deprecated: Cart-based campaign validation (removed in PR 2) ───
  // isEligibleCampaignSession() was used to validate cart discount codes against
  // stored UTM. This is no longer needed since resolveActiveEligibleDiscountCode()
  // now directly returns stored UTM as the sole campaign authority.

  function clearVisibleCouponPrices() {
    var activePrices = document.querySelectorAll('.price--coupon-active');

    for (var i = 0; i < activePrices.length; i++) {
      var priceRoot = activePrices[i];
      priceRoot.classList.remove('price--coupon-active');

      var couponContainer = priceRoot.querySelector('.price__coupon');
      var regularContainer = priceRoot.querySelector('.price__regular');
      var saleContainer = priceRoot.querySelector('.price__sale');

      if (couponContainer) couponContainer.classList.add('hidden');
      if (regularContainer) regularContainer.classList.remove('hidden');
      if (saleContainer) saleContainer.classList.remove('hidden');
    }

    var activeCards = document.querySelectorAll('.card--coupon-active');
    for (var j = 0; j < activeCards.length; j++) {
      activeCards[j].classList.remove('card--coupon-active');
      var visualFlag = activeCards[j].querySelector('[data-coupon-visual-flag]');
      if (visualFlag) visualFlag.remove();
    }
  }

  /**
   * Clears in-memory discount state and visible coupon prices.
   * Does NOT clear localStorage UTM - stored campaign persists across sessions.
   * Only called explicitly, not driven by cart state.
   */
  function clearInactiveDiscountState() {
    discountCode = null;
    clearVisibleCouponPrices();
  }

  function shouldShowCouponFlag() {
    var config = window.__storefrontApi;
    return !config || config.couponFlagEnabled !== false;
  }

  /**
   * Resolves the active discount code from stored UTM source.
   * After initial page-load capture, resolver reads storage only so stale page
   * URLs do not overwrite a newer UTM from another tab.
   */
  function resolveActiveEligibleDiscountCode() {
    return setActiveDiscountCode(getStoredUtmSource());
  }

  /**
   * Captures URL utm_source at most once per page load, then uses storage as
   * the single active campaign code for shadow-cart simulations.
   */
  function initializeActiveEligibleDiscountCode() {
    return setActiveDiscountCode(captureInitialUrlUtmSource());
  }

  function resolveStoredEligibleDiscountCode(value) {
    var utmSource = typeof value === 'undefined' ? getStoredUtmSource() : normalizeUtmSource(value);
    return setActiveDiscountCode(utmSource);
  }

  function handleStorageUtmChange(newValue) {
    clearTimeout(storageRevalidationTimer);
    storageRevalidationTimer = setTimeout(function() {
      // Capture old code BEFORE updating (critical: order matters)
      var oldCode = discountCode;
      
      // Read new UTM value and re-initialize pricing checks
      var newCode = resolveStoredEligibleDiscountCode(newValue);
      
      if (newCode) {
        clearVisibleCouponPrices();
        initPLP();
        initPDP();
        // Handle material state change for cart sync (pass oldCode explicitly)
        handleMaterialStateChange(newCode, oldCode);
      } else {
        // No UTM: clear all coupon pricing and reset cart sync
        clearInactiveDiscountState();
        resetCartDiscountSyncState();
      }
    }, 50);
  }

  // ─── Deprecated: scheduleStorageRevalidation() renamed in PR 2 ───
  // The old function only re-read UTM without triggering re-rendering.
  // handleStorageUtmChange() now properly re-initializes PLP/PDP pricing.

  // ─── Deprecated: Cart-based discount detection (removed in PR 2) ───
  // The following functions were removed because the campaign authority is now
  // UTM-only (stored in localStorage as coupon-discount-utm-source).
  // No real-cart validation or clearing is performed.
  //
  // REMOVED:
  // - storeDiscountCode() - stored cart-based discount code (unused key)
  // - resolveDiscountCodeFromCart() - fetched /cart.js for active discounts
  //
  // The campaign session is determined solely by URL utm_source persisted to
  // localStorage. See resolveActiveEligibleDiscountCode() for the current flow.

  // ─── Helpers ─────────────────────────────────────────────────

  /**
   * Converts numeric variant ID to Shopify GID format
   * Example: "12345" → "gid://shopify/ProductVariant/12345"
   */
  function toGID(variantId) {
    return 'gid://shopify/ProductVariant/' + variantId;
  }

  /**
   * Extracts numeric ID from Shopify GID
   * Example: "gid://shopify/ProductVariant/12345" → "12345"
   */
  function fromGID(gid) {
    return gid.split('/').pop();
  }

  /**
   * Formats price using shop money_format
   * Cached for performance
   */
  function formatMoney(amountInUnits, moneyFormat) {
    var cacheKey = amountInUnits + '|' + moneyFormat;

    if (formatMoneyCache[cacheKey]) {
      return formatMoneyCache[cacheKey];
    }

    var amount = amountInUnits;
    var formatted = amount.toFixed(2);
    var parts = formatted.split('.');
    var dollars = parts[0];
    var cents = parts[1];

    // EU format: dot thousands separator, comma decimal
    var withComma = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + cents;

    // US format: comma thousands separator, dot decimal
    var withDot = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '.' + cents;

    // Swiss format: apostrophe thousands separator
    var withApostrophe = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, "'") + '.' + cents;

    // No decimals formats
    var rounded = Math.round(amount).toString();
    var roundedWithComma = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var roundedWithDot = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var roundedWithApostrophe = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, "'");

    var result = moneyFormat
      .replace('{{amount_with_apostrophe_separator}}', withApostrophe)
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', roundedWithComma)
      .replace('{{amount_no_decimals_with_apostrophe_separator}}', roundedWithApostrophe)
      .replace('{{amount_no_decimals}}', roundedWithDot)
      .replace('{{ amount }}', withDot)
      .replace('{{ amount_with_comma_separator }}', withComma)
      .replace('{{amount}}', withDot);

    formatMoneyCache[cacheKey] = result;
    return result;
  }

  // ─── Cache Management ────────────────────────────────────────

  /**
   * Gets cached discounts for current discount code
   * Returns null if cache expired or unavailable
   */
  function getCachedDiscounts() {
    if (!discountCode) return null;

    try {
      var cacheKey = CACHE_KEY_PREFIX + discountCode;
      var raw = localStorage.getItem(cacheKey);

      if (!raw) return null;

      var data = JSON.parse(raw);
      var isExpired = Date.now() - data.timestamp > CACHE_TTL;

      if (isExpired) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return data.discounts;
    } catch (e) {
      return null;
    }
  }

  /**
   * Saves discounts to cache with timestamp
   */
  function setCachedDiscounts(discounts) {
    if (!discountCode) return;

    try {
      var cacheKey = CACHE_KEY_PREFIX + discountCode;
      localStorage.setItem(cacheKey, JSON.stringify({
        timestamp: Date.now(),
        discounts: discounts
      }));
    } catch (e) {
      // localStorage quota exceeded or unavailable
    }
  }

  // ─── DOM Collection ──────────────────────────────────────────

  /**
   * Collects variant IDs from PLP product cards
   * Returns array of unique variant IDs
   */
  function collectPLPVariantIds() {
    var cards = document.querySelectorAll('[data-coupon-product-card]');
    var seen = {};
    var ids = [];

    for (var i = 0; i < cards.length; i++) {
      var id = cards[i].getAttribute('data-variant-id');
      if (id && !seen[id]) {
        seen[id] = true;
        ids.push(id);
      }
    }

    return ids;
  }

  /**
   * Gets current variant ID from PDP product-info element
   */
  function getPDPVariantId() {
    var productInfo = document.querySelector('product-info[data-current-variant-id]');
    if (!productInfo) return null;

    return productInfo.getAttribute('data-current-variant-id');
  }

  // ─── Storefront API ──────────────────────────────────────────

  /**
   * GraphQL mutation for creating shadow cart
   */
  var CART_CREATE_MUTATION = [
    'mutation cartCreate($lines: [CartLineInput!]!, $discountCodes: [String!]) {',
    '  cartCreate(input: { lines: $lines, discountCodes: $discountCodes }) {',
    '    cart {',
    '      lines(first: 250) {',
    '        edges {',
    '          node {',
    '            merchandise {',
    '              ... on ProductVariant {',
    '                id',
    '              }',
    '            }',
    '            cost {',
    '              amountPerQuantity {',
    '                amount',
    '                currencyCode',
    '              }',
    '            }',
    '            discountAllocations {',
    '              discountedAmount {',
    '                amount',
    '                currencyCode',
    '              }',
    '              ... on CartAutomaticDiscountAllocation {',
    '                title',
    '              }',
    '              ... on CartCodeDiscountAllocation {',
    '                code',
    '              }',
    '            }',
    '          }',
    '        }',
    '      }',
    '    }',
    '    userErrors {',
    '      field',
    '      message',
    '    }',
    '  }',
    '}'
  ].join('\n');

  /**
   * Calls Storefront API with timeout
   */
  function callStorefrontAPI(shopDomain, token, query, variables) {
    var url = 'https://' + shopDomain + '/api/' + API_VERSION + '/graphql.json';

    var controller = new AbortController();
    var timeoutId = setTimeout(function() {
      controller.abort();
    }, FETCH_TIMEOUT);

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token
      },
      body: JSON.stringify({ query: query, variables: variables }),
      signal: controller.signal
    })
    .then(function(response) {
      clearTimeout(timeoutId);
      if (!response.ok) return null;
      return response.json();
    })
    .catch(function(error) {
      clearTimeout(timeoutId);
      if (error.name !== 'AbortError') {
        console.warn('[CouponDiscount] API call failed:', error);
      }
      return null;
    });
  }

  /**
   * Parses Storefront API response to extract discount data per variant
   */
  function parseDiscounts(responseData) {
    var discounts = {};

    if (!responseData || !responseData.data) return discounts;

    var cart = responseData.data.cartCreate;
    if (!cart || !cart.cart || !cart.cart.lines) return discounts;

    var edges = cart.cart.lines.edges;

    for (var i = 0; i < edges.length; i++) {
      var line = edges[i].node;
      var variantId = fromGID(line.merchandise.id);
      var allocations = line.discountAllocations;

      if (!allocations || allocations.length === 0) continue;

      var totalDiscount = 0;
      var discountTitle = '';

      for (var j = 0; j < allocations.length; j++) {
        var amount = parseFloat(allocations[j].discountedAmount.amount);
        if (!Number.isFinite(amount) || amount <= 0) continue;

        totalDiscount += amount;

        // Prefer code over title for coupon discounts
        if (!discountTitle) {
          discountTitle = allocations[j].code || allocations[j].title || '';
        }
      }

      if (totalDiscount <= 0) continue;

      var originalPrice = parseFloat(line.cost.amountPerQuantity.amount);
      if (!Number.isFinite(originalPrice) || originalPrice <= 0) continue;

      var finalPrice = originalPrice - totalDiscount;
      var discountRate = totalDiscount / originalPrice;
      var percent = Math.round((totalDiscount / originalPrice) * 100);

      discounts[variantId] = {
        percent: percent,
        rate: discountRate,
        originalPrice: originalPrice,
        finalPrice: finalPrice,
        totalDiscount: totalDiscount,
        title: discountTitle
      };
    }

    return discounts;
  }

  /**
   * Fetches batch of variants with retry logic for invalid variants
   */
  function fetchBatch(lines, shopDomain, token, retryCount) {
    retryCount = retryCount || 0;
    if (lines.length === 0) return Promise.resolve({});

    var variables = { lines: lines };
    
    // Add discountCodes to the mutation if we have an active discount code
    if (discountCode) {
      variables.discountCodes = [discountCode];
    }

    return callStorefrontAPI(shopDomain, token, CART_CREATE_MUTATION, variables)
      .then(function(data) {
        if (!data) return {};

        var cartCreate = data.data && data.data.cartCreate;

        // Handle user errors (invalid variants)
        if (cartCreate && !cartCreate.cart && cartCreate.userErrors && cartCreate.userErrors.length > 0) {
          var failingIndices = {};
          cartCreate.userErrors.forEach(function(err) {
            if (err.field && err.field[1] === 'lines') {
              failingIndices[parseInt(err.field[2], 10)] = true;
            }
          });

          var cleanLines = lines.filter(function(_, i) {
            return !failingIndices[i];
          });

          if (cleanLines.length < lines.length && retryCount < MAX_RETRIES) {
            return fetchBatch(cleanLines, shopDomain, token, retryCount + 1);
          }

          if (retryCount >= MAX_RETRIES) {
            console.warn('[CouponDiscount] Max retries reached, some variants may be invalid');
          }

          return {};
        }

        return parseDiscounts(data);
      });
  }

  /**
   * Fetches discounts for variant IDs
   * Handles batching for >250 variants
   */
  function fetchDiscounts(variantIds, shopDomain, token) {
    var lines = variantIds.map(function(id) {
      return { merchandiseId: toGID(id), quantity: 1 };
    });

    if (lines.length <= BATCH_SIZE) {
      return fetchBatch(lines, shopDomain, token);
    }

    // Parallel batches for >250 variants
    var batches = [];
    for (var i = 0; i < lines.length; i += BATCH_SIZE) {
      batches.push(lines.slice(i, i + BATCH_SIZE));
    }

    return Promise.all(batches.map(function(batch) {
      return fetchBatch(batch, shopDomain, token);
    })).then(function(results) {
      var merged = {};
      for (var i = 0; i < results.length; i++) {
        var parsed = results[i];
        for (var key in parsed) {
          if (parsed.hasOwnProperty(key)) {
            merged[key] = parsed[key];
          }
        }
      }
      return merged;
    });
  }

  // ─── Best Discount Comparison ─────────────────────────────────

  /**
   * Determines which discount is better: native sale or coupon
   * Returns true if coupon discount is better than native sale
   */
  function couponIsBetter(variantId, discount, cardOrPriceRoot) {
    var display = getCouponDisplayPrices(discount, cardOrPriceRoot);
    var currentPrice = display.currentPriceCents;

    if (!display.finalPriceCents || !currentPrice) return false;

    // Coupon wins only if its final visual price is lower than the native current price.
    return display.finalPriceCents < currentPrice;
  }

  /**
   * Computes visual coupon prices using competition logic, not stacked logic.
   * If the product has a native sale, the coupon competes against compare_at_price.
   * This avoids showing coupon discount applied on top of an existing sale price.
   */
  function getCouponDisplayPrices(discount, cardOrPriceRoot) {
    var originalPriceValue = cardOrPriceRoot.getAttribute('data-coupon-original-price') ||
      cardOrPriceRoot.getAttribute('data-compare-at-price') ||
      '0';
    var currentPriceValue = cardOrPriceRoot.getAttribute('data-coupon-current-price') ||
      cardOrPriceRoot.getAttribute('data-product-price') ||
      '0';
    var originalPriceCents = parseInt(originalPriceValue, 10);
    var currentPriceCents = parseInt(currentPriceValue, 10);

    if (!Number.isFinite(originalPriceCents)) originalPriceCents = 0;
    if (!Number.isFinite(currentPriceCents)) currentPriceCents = 0;

    var hasNativeSale = originalPriceCents > currentPriceCents && currentPriceCents > 0;
    var basePriceCents = hasNativeSale ? originalPriceCents : currentPriceCents;
    var rate = typeof discount.rate === 'number' ? discount.rate : (discount.percent || 0) / 100;
    var finalPriceCents = Math.round(basePriceCents * (1 - rate));

    return {
      currentPriceCents: currentPriceCents,
      basePriceCents: basePriceCents,
      finalPriceCents: finalPriceCents,
      hasNativeSale: hasNativeSale
    };
  }

  function updateCouponBadge(card, discount) {
    if (!card || !discount) return;

    var label = discount.percent + '%';
    if (discount.title) {
      label += ' ' + discount.title;
    }

    card.classList.add('card--coupon-active');

    // Hide Dawn badge regions entirely when coupon pricing wins. The theme has
    // duplicate `.card__badge` containers for different layouts, and showing
    // them alongside the visual image flag creates duplicated campaign labels.
    var badgeContainers = card.querySelectorAll('.card__badge');
    for (var j = 0; j < badgeContainers.length; j++) {
      badgeContainers[j].classList.add('hidden');
      badgeContainers[j].style.display = 'none';
    }

    // The theme/app also renders custom sale flags outside `.card__badge` as
    // `.hc-sale-tag`. Do NOT convert those nodes: their CSS/structure can add
    // a native discount prefix (for example "-") and visually conflict with
    // the coupon label. Hide them and render an independent coupon flag.
    var customSaleTags = card.querySelectorAll('.hc-sale-tag');
    for (var k = 0; k < customSaleTags.length; k++) {
      customSaleTags[k].classList.add('hidden');
      customSaleTags[k].style.display = 'none';
    }

    var visualCard = card.querySelector('.card__inner') || card.querySelector('.card') || card;
    var couponFlag = visualCard.querySelector('[data-coupon-visual-flag]');

    if (!shouldShowCouponFlag()) {
      if (couponFlag) couponFlag.remove();
      return;
    }

    if (!couponFlag) {
      couponFlag = document.createElement('div');
      couponFlag.className = 'coupon-discount-flag';
      couponFlag.setAttribute('data-coupon-visual-flag', '');
      visualCard.insertBefore(couponFlag, visualCard.firstChild);
    }

    couponFlag.textContent = label;
    couponFlag.classList.remove('hidden');
    couponFlag.style.display = '';
  }

  // ─── DOM Painting ────────────────────────────────────────────

  function clearPLPCouponState(card, priceRoot) {
    if (priceRoot) {
      priceRoot.classList.remove('price--coupon-active');

      var couponContainer = priceRoot.querySelector('.price__coupon');
      var regularContainer = priceRoot.querySelector('.price__regular');
      var saleContainer = priceRoot.querySelector('.price__sale');

      if (couponContainer) couponContainer.classList.add('hidden');
      if (regularContainer) regularContainer.classList.remove('hidden');
      if (saleContainer) saleContainer.classList.remove('hidden');
    }

    if (card) {
      card.classList.remove('card--coupon-active');

      var visualFlags = card.querySelectorAll('[data-coupon-visual-flag]');
      for (var i = 0; i < visualFlags.length; i++) {
        visualFlags[i].remove();
      }
    }
  }

  /**
   * Updates PLP card price with coupon strikethrough
   */
  function updatePLPPrice(card, discount, moneyFormat) {
    var priceRoot = card.querySelector('[data-coupon-price-root]');
    if (!priceRoot) return;

    // Check if coupon is better than native sale
    if (!couponIsBetter(null, discount, priceRoot)) {
      // Native sale is better: clear any prior coupon paint for this card
      clearPLPCouponState(card, priceRoot);
      return;
    }

    var displayPrices = getCouponDisplayPrices(discount, priceRoot);

    // Coupon is better: use dedicated coupon container
    var priceContainer = priceRoot.querySelector('.price__container');
    if (!priceContainer) return;

    // Hide native containers (do NOT mutate or depend on them)
    var regularContainer = priceContainer.querySelector('.price__regular');
    var saleContainer = priceContainer.querySelector('.price__sale');
    if (regularContainer) regularContainer.classList.add('hidden');
    if (saleContainer) saleContainer.classList.add('hidden');

    // Show dedicated coupon container
    var couponContainer = priceContainer.querySelector('.price__coupon');
    if (!couponContainer) return;
    couponContainer.classList.remove('hidden');
    priceRoot.classList.add('price--coupon-active');

    // Populate strikethrough element (current price if native sale exists, otherwise original)
    var strikethroughEl = couponContainer.querySelector('[data-coupon-current-strikethrough]');
    if (strikethroughEl) {
      strikethroughEl.textContent = formatMoney(displayPrices.basePriceCents / 100, moneyFormat);
    }

    // Populate final price element
    var finalPriceEl = couponContainer.querySelector('[data-coupon-final-price]');
    if (finalPriceEl) {
      finalPriceEl.textContent = formatMoney(displayPrices.finalPriceCents / 100, moneyFormat);
      finalPriceEl.removeAttribute('hidden');

      // Add ARIA label for accessibility
      finalPriceEl.setAttribute('aria-label',
        'Original price ' + formatMoney(displayPrices.basePriceCents / 100, moneyFormat) +
        ', now ' + formatMoney(displayPrices.finalPriceCents / 100, moneyFormat) +
        ' with ' + discount.percent + '% discount');
    }

    updateCouponBadge(card, discount);
  }

  /**
   * Updates PDP price with coupon strikethrough
   */
  function updatePDPPrice(variantId, discount, moneyFormat) {
    var productInfo = document.querySelector('product-info[data-current-variant-id="' + variantId + '"]');
    if (!productInfo) return;

    var priceRoot = productInfo.querySelector('[data-coupon-price-root]');
    if (!priceRoot) return;

    var priceContainer = priceRoot.querySelector('.price__container');
    var displayPrices = getCouponDisplayPrices(discount, priceRoot);

    // Check if coupon is better
    if (!couponIsBetter(variantId, discount, priceRoot)) {
      // Coupon lost or not applicable: clear coupon state and restore native
      priceRoot.classList.remove('price--coupon-active');
      if (priceContainer) {
        var couponContainer = priceContainer.querySelector('.price__coupon');
        if (couponContainer) couponContainer.classList.add('hidden');
        var regularContainer = priceContainer.querySelector('.price__regular');
        var saleContainer = priceContainer.querySelector('.price__sale');
        if (regularContainer) regularContainer.classList.remove('hidden');
        if (saleContainer) saleContainer.classList.remove('hidden');
      }
      return;
    }

    // Coupon is better: use dedicated coupon container
    if (!priceContainer) return;

    // Hide native containers (do NOT mutate or depend on them)
    var regularContainer = priceContainer.querySelector('.price__regular');
    var saleContainer = priceContainer.querySelector('.price__sale');
    if (regularContainer) regularContainer.classList.add('hidden');
    if (saleContainer) saleContainer.classList.add('hidden');

    // Show dedicated coupon container
    var couponContainer = priceContainer.querySelector('.price__coupon');
    if (!couponContainer) return;
    couponContainer.classList.remove('hidden');
    priceRoot.classList.add('price--coupon-active');

    // Populate strikethrough element (current price if native sale exists, otherwise original)
    var strikethroughEl = couponContainer.querySelector('[data-coupon-current-strikethrough]');
    if (strikethroughEl) {
      strikethroughEl.textContent = formatMoney(displayPrices.basePriceCents / 100, moneyFormat);
    }

    // Populate final price element
    var finalPriceEl = couponContainer.querySelector('[data-coupon-final-price]');
    if (finalPriceEl) {
      finalPriceEl.textContent = formatMoney(displayPrices.finalPriceCents / 100, moneyFormat);
      finalPriceEl.removeAttribute('hidden');

      // Add ARIA label for accessibility
      finalPriceEl.setAttribute('aria-label',
        'Original price ' + formatMoney(displayPrices.basePriceCents / 100, moneyFormat) +
        ', now ' + formatMoney(displayPrices.finalPriceCents / 100, moneyFormat) +
        ' with ' + discount.percent + '% discount');
    }
  }

  function syncPDPPriceRootFromVariant(variantId, variant) {
    if (!variant) return;

    var productInfo = document.querySelector('product-info[data-current-variant-id]');
    if (!productInfo) return;

    var priceRoot = productInfo.querySelector('[data-coupon-price-root]');
    if (!priceRoot) return;

    priceRoot.setAttribute('data-coupon-variant-id', String(variantId));

    if (typeof variant.price !== 'undefined' && variant.price !== null) {
      priceRoot.setAttribute('data-coupon-current-price', String(variant.price));
    }

    if (typeof variant.compare_at_price !== 'undefined' && variant.compare_at_price !== null) {
      priceRoot.setAttribute('data-coupon-original-price', String(variant.compare_at_price || 0));
    }
  }

  function buildPDPVariantEventFromDOM(variantId) {
    var variant = { id: variantId };
    var productInfo = document.querySelector('product-info[data-current-variant-id]');
    var priceRoot = productInfo ? productInfo.querySelector('[data-coupon-price-root]') : null;

    if (priceRoot) {
      var priceRootVariantId = priceRoot.getAttribute('data-coupon-variant-id');
      var currentPrice = priceRoot.getAttribute('data-coupon-current-price');
      var originalPrice = priceRoot.getAttribute('data-coupon-original-price');

      if (String(priceRootVariantId) === String(variantId)) {
        if (currentPrice) variant.price = currentPrice;
        if (originalPrice) variant.compare_at_price = originalPrice;
      }
    }

    return {
      data: {
        variant: variant
      }
    };
  }

  function schedulePDPVariantFallback(variantId) {
    var attempt = arguments.length > 1 ? arguments[1] : 0;

    clearTimeout(pdpFallbackTimer);
    pdpFallbackTimer = setTimeout(function() {
      var event = buildPDPVariantEventFromDOM(variantId);
      var variant = event.data.variant;
      var hasFreshPrice = typeof variant.price !== 'undefined' && variant.price !== null;

      if (!hasFreshPrice && attempt < 5) {
        schedulePDPVariantFallback(variantId, attempt + 1);
        return;
      }

      handleVariantChange(event);
    }, attempt === 0 ? 50 : 100);
  }

  /**
   * Applies discounts to PLP cards
   */
  function applyPLPDiscounts(discounts, moneyFormat, targetCards) {
    var cards = targetCards || document.querySelectorAll('[data-coupon-product-card]');

    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var variantId = card.getAttribute('data-variant-id');
      var discount = discounts[variantId];

      if (!discount) continue;

      updatePLPPrice(card, discount, moneyFormat);
    }
  }

  // ─── PDP Variant Change Handler ──────────────────────────────

/**
    * Handles variantChange event from Dawn theme pub/sub
    */
  function handleVariantChange(event) {
    var config = window.__storefrontApi;
    if (!config || !config.token) return;

    // Dawn pub/sub event structure: { data: { sectionId, html, variant } }
    var data = event.data;
    if (!data || !data.variant) return;

    var variantId = String(data.variant.id);

    // Update product-info data attribute
    var productInfo = document.querySelector('product-info[data-current-variant-id]');
    if (productInfo) {
      productInfo.setAttribute('data-current-variant-id', variantId);
    }

    syncPDPPriceRootFromVariant(variantId, data.variant);

    // Use stored UTM as discount code for variant-level pricing checks.
    // No cart revalidation - UTM/localStorage is the authority.
    var activeCode = resolveActiveEligibleDiscountCode();
    if (!activeCode) return;

    // Check cache first
    var cached = getCachedDiscounts();
    if (cached && cached[variantId]) {
      updatePDPPrice(variantId, cached[variantId], config.moneyFormat);
      queriedVariantIds[variantId] = true;
      return;
    }

    // Fetch if not cached
    if (queriedVariantIds[variantId]) return;

    queriedVariantIds[variantId] = true;

    fetchDiscounts([variantId], config.shopDomain, config.token)
      .then(function(discounts) {
        var merged = Object.assign({}, cached || {}, discounts);
        setCachedDiscounts(merged);

        if (discounts[variantId]) {
          updatePDPPrice(variantId, discounts[variantId], config.moneyFormat);
        }
      });
  }

/**
    * Subscribes to Dawn theme variantChange pub/sub event
    * Handles multiple script loading contexts (window vs bare globals)
    */
  function subscribeToVariantChange() {
    var subscribed = false;
    
    // Try bare globals first (Dawn theme defines PUB_SUB_EVENTS and subscribe as top-level const/function)
    // In classic browser scripts, top-level const/function declarations are NOT exposed as window properties
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
        subscribed = true;
      }
    } catch (e) {
      // Bare globals not accessible (e.g., module scope or strict mode cross-script)
    }
    
    // Try window properties (for themes that expose globals explicitly)
    if (!subscribed) {
      try {
        if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
          window.subscribe(window.PUB_SUB_EVENTS.variantChange, handleVariantChange);
          subscribed = true;
        }
      } catch (e) {
        // Window globals not accessible
      }
    }
    
    // Fallback: MutationObserver for data-current-variant-id changes
    // Used when pub/sub is unavailable or subscription fails
    if (!subscribed) {
      var productInfo = document.querySelector('product-info[data-current-variant-id]');
      if (productInfo) {
        var observer = new MutationObserver(function(mutations) {
          for (var i = 0; i < mutations.length; i++) {
            var mutation = mutations[i];
            if (mutation.attributeName === 'data-current-variant-id') {
              var variantId = productInfo.getAttribute('data-current-variant-id');
              if (variantId) {
                schedulePDPVariantFallback(variantId);
              }
            }
          }
        });
        observer.observe(productInfo, { attributes: true });
      }
      
      // Also listen for legacy variant:update event as additional fallback
      document.addEventListener('variant:update', function(event) {
        var detail = event.detail;
        if (!detail || !detail.resource) return;
        
        var variantId = detail.resource.id;
        var currentPrice = detail.resource.price;
        var originalPrice = detail.resource.compare_at_price;
        handleVariantChange({
          data: {
            variant: {
              id: variantId,
              price: currentPrice,
              compare_at_price: originalPrice
            }
          }
        });
      }, true);
    }
  }

  // ─── Real Cart Discount Sync Triggers ────────────────────────────

  /**
   * Debounced wrapper for cart discount sync triggers.
   * Coalesces rapid cart updates into a single sync call.
   */
  function debouncedCartSync(reason) {
    if (cartDiscountSync.debounceTimer) {
      clearTimeout(cartDiscountSync.debounceTimer);
    }
    
    cartDiscountSync.debounceTimer = setTimeout(function() {
      cartDiscountSync.debounceTimer = null;
      ensureCartDiscountApplied({ reason: reason });
    }, CART_SYNC_DEBOUNCE);
  }

  /**
   * Subscribes to Dawn theme cartUpdate pub/sub event.
   * Triggers discount sync on add-to-cart and cart quantity changes.
   * Handles multiple script loading contexts (window vs bare globals).
   */
  function subscribeToCartUpdate() {
    var subscribed = false;
    
    // Try bare globals first (Dawn theme defines PUB_SUB_EVENTS and subscribe as top-level const/function)
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function(event) {
          debouncedCartSync('cart-update');
        });
        subscribed = true;
      }
    } catch (e) {
      // Bare globals not accessible
    }
    
    // Try window properties (for themes that expose globals explicitly)
    if (!subscribed) {
      try {
        if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
          window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function(event) {
            debouncedCartSync('cart-update');
          });
          subscribed = true;
        }
      } catch (e) {
        // Window globals not accessible
      }
    }
  }

  /**
   * Triggers cart discount sync on cart page load.
   * Called after visual pricing initialization on cart page.
   */
  function triggerCartPageSync() {
    // Check if we're on the cart page by looking for cart elements
    var cartItems = document.querySelector('cart-items') || 
                    document.querySelector('.cart-items') ||
                    document.querySelector('[data-cart-items]');
    var cartForm = document.querySelector('form[action*="/cart"]');
    
    if (cartItems || cartForm) {
      ensureCartDiscountApplied({ reason: 'cart-page' });
    }
  }

/**
    * Sets up checkout intent listener.
    * Triggers discount sync once before checkout navigation.
    * 
    * Bounded behavior:
    * - Uses capture phase to run before form submit/navigation
    * - In-flight lock in ensureCartDiscountApplied prevents duplicate calls
    * - Does NOT await the sync Promise (checkout proceeds without blocking)
    * - WARNING: If network is slow, sync may not complete before checkout redirect
    *   This is acceptable: checkout page itself can re-trigger sync via cart-page load
    */
  function setupCheckoutIntentListener() {
    // Listen for checkout button click (form submit with name="checkout")
    var checkoutButtons = document.querySelectorAll('[name="checkout"]');
    
    for (var i = 0; i < checkoutButtons.length; i++) {
      checkoutButtons[i].addEventListener('click', function(e) {
        // Fire sync in capture phase before form submit
        // The sync runs async - form submit continues without blocking
        // In-flight lock prevents duplicate calls from rapid clicks
        if (getStoredUtmSource()) {
          ensureCartDiscountApplied({ reason: 'checkout' });
        }
      }, true); // Capture phase: runs before form submit
    }
    
    // Also listen for checkout links (a[href*='/checkouts/'])
    var checkoutLinks = document.querySelectorAll('a[href*="/checkouts/"]');
    
    for (var j = 0; j < checkoutLinks.length; j++) {
      checkoutLinks[j].addEventListener('click', function(e) {
        if (getStoredUtmSource()) {
          ensureCartDiscountApplied({ reason: 'checkout' });
        }
      }, true);
    }
  }

/**
    * Handles material state changes that should re-enable sync.
    * - UTM change: resets sync state and triggers new sync
    * - Cart token change: resets sync state (discovered on next /cart.js call)
    * 
    * @param {string} newUtmCode - The new UTM code value
    * @param {string} oldUtmCode - The previous UTM code (passed explicitly from handleStorageUtmChange)
    */
  function handleMaterialStateChange(newUtmCode, oldUtmCode) {
    // Use passed oldCode for accurate comparison (fallback to current for direct calls)
    var oldCode = typeof oldUtmCode !== 'undefined' ? oldUtmCode : discountCode;
    
    // UTM changed: reset sync state for new campaign
    if (oldCode !== newUtmCode) {
      resetCartDiscountSyncState();
      // Trigger sync for new UTM if we have items in cart
      if (newUtmCode) {
        debouncedCartSync('utm-change');
      }
    }
  }

  // ─── MutationObserver for AJAX-loaded Cards ───────────────────

  /**
   * Observes new product cards added via AJAX
   */
  function observeNewCards() {
    var config = window.__storefrontApi;
    if (!config || !config.token || !discountCode) return;

    var container = document.querySelector('.product-grid') ||
                    document.querySelector('[product-grid]') ||
                    document.querySelector('#MainContent') ||
                    document.body;

    var observer = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var addedNodes = mutations[i].addedNodes;
        for (var j = 0; j < addedNodes.length; j++) {
          var node = addedNodes[j];
          if (node.nodeType !== 1) continue;

          if (node.matches && node.matches('[data-coupon-product-card]')) {
            var id = node.getAttribute('data-variant-id');
            if (id) pendingMutationIds.push(id);
          }

          if (node.querySelector) {
            var innerCards = node.querySelectorAll('[data-coupon-product-card]');
            for (var k = 0; k < innerCards.length; k++) {
              var innerId = innerCards[k].getAttribute('data-variant-id');
              if (innerId) pendingMutationIds.push(innerId);
            }
          }
        }
      }

      if (pendingMutationIds.length === 0) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() {
        var ids = pendingMutationIds.slice();
        pendingMutationIds = [];
        handleNewCards(config, ids);
      }, MUTATION_DEBOUNCE);
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  /**
   * Handles newly added product cards
   */
  function handleNewCards(config, mutationIds) {
    var cached = getCachedDiscounts() || {};

    // Find new cards by IDs
    var newCards = [];
    for (var i = 0; i < mutationIds.length; i++) {
      var card = document.querySelector('[data-coupon-product-card][data-variant-id="' + mutationIds[i] + '"]');
      if (card) newCards.push(card);
    }

    // Dedupe and filter IDs not yet queried
    var seen = {};
    var newIds = [];
    for (var j = 0; j < mutationIds.length; j++) {
      var id = mutationIds[j];
      if (!seen[id] && !queriedVariantIds[id]) {
        seen[id] = true;
        newIds.push(id);
      }
    }

    // Apply cached results to new cards
    if (Object.keys(cached).length > 0 && newCards.length > 0) {
      applyPLPDiscounts(cached, config.moneyFormat, newCards);
    }

    if (newIds.length === 0) return;

    newIds.forEach(function(id) {
      queriedVariantIds[id] = true;
    });

    fetchDiscounts(newIds, config.shopDomain, config.token)
      .then(function(newDiscounts) {
        var merged = Object.assign({}, cached, newDiscounts);
        setCachedDiscounts(merged);
        applyPLPDiscounts(merged, config.moneyFormat, newCards);
      });
  }

  // ─── Hover Prefetch ──────────────────────────────────────────

  /**
   * Prefetches discounts on hover for better UX
   */
  function initHoverPrefetch() {
    var config = window.__storefrontApi;
    if (!config || !config.token || !discountCode) return;

    document.addEventListener('mouseover', function(e) {
      var target = e.target;
      var card = target.closest('[data-coupon-product-card]');
      if (!card) return;

      var variantId = card.getAttribute('data-variant-id');
      if (!variantId || queriedVariantIds[variantId]) return;

      queriedVariantIds[variantId] = true;
      hoverPrefetchQueue.push(variantId);

      clearTimeout(hoverPrefetchTimer);
      hoverPrefetchTimer = setTimeout(function() {
        processHoverBatch(config);
      }, HOVER_PREFETCH_DELAY);
    }, { passive: true });
  }

  /**
   * Processes batch of hover-prefetched variants
   */
  function processHoverBatch(config) {
    if (hoverPrefetchQueue.length === 0) return;

    var idsToFetch = hoverPrefetchQueue.slice();
    hoverPrefetchQueue = [];

    fetchDiscounts(idsToFetch, config.shopDomain, config.token)
      .then(function(newDiscounts) {
        var cached = getCachedDiscounts() || {};
        setCachedDiscounts(Object.assign({}, cached, newDiscounts));
      });
  }

  // ─── Initialization ──────────────────────────────────────────

  /**
   * Initializes PLP discount checks
   */
  function initPLP() {
    var config = window.__storefrontApi;
    if (!config || !config.token || !discountCode) return;

    var variantIds = collectPLPVariantIds();
    if (variantIds.length === 0) return;

    // Apply cached results immediately
    var cached = getCachedDiscounts() || {};
    if (Object.keys(cached).length > 0) {
      applyPLPDiscounts(cached, config.moneyFormat);
    }

    // Filter to only variants NOT in cache (respect cache as source of truth)
    var uncachedIds = variantIds.filter(function(id) {
      return !cached[id];
    });

    // If all variants are cached, no additional API calls needed
    if (uncachedIds.length === 0) {
      // Mark all as queried to prevent duplicate fetches
      variantIds.forEach(function(id) {
        queriedVariantIds[id] = true;
      });
      return;
    }

    // Mark uncached variants as queried
    uncachedIds.forEach(function(id) {
      queriedVariantIds[id] = true;
    });

    // Fetch only missing variants
    fetchDiscounts(uncachedIds, config.shopDomain, config.token)
      .then(function(discounts) {
        var merged = Object.assign({}, cached, discounts);
        setCachedDiscounts(merged);
        applyPLPDiscounts(discounts, config.moneyFormat);
      });
  }

  /**
   * Initializes PDP discount checks
   */
  function initPDP() {
    var config = window.__storefrontApi;
    if (!config || !config.token || !discountCode) return;

    var variantId = getPDPVariantId();
    if (!variantId) return;

    // Check cache first
    var cached = getCachedDiscounts();
    if (cached && cached[variantId]) {
      updatePDPPrice(variantId, cached[variantId], config.moneyFormat);
      queriedVariantIds[variantId] = true;
      return;
    }

    // Fetch if not cached
    queriedVariantIds[variantId] = true;

    fetchDiscounts([variantId], config.shopDomain, config.token)
      .then(function(discounts) {
        var merged = Object.assign({}, cached || {}, discounts);
        setCachedDiscounts(merged);

        if (discounts[variantId]) {
          updatePDPPrice(variantId, discounts[variantId], config.moneyFormat);
        }
      });
  }

/**
    * Main initialization entry point
    * UTM from URL/localStorage is the sole authority for campaign pricing.
    * No real-cart coupon validation required.
    * Real cart discount sync is triggered by material events only.
    */
  function init() {
    var code = initializeActiveEligibleDiscountCode();
    if (!code) return;

    initPLP();
    initPDP();
    initHoverPrefetch();
    observeNewCards();
    subscribeToVariantChange();
    
    // Real cart discount sync triggers (Phase 8 PR 2)
    // Subscribe to Dawn cartUpdate pub/sub for add-to-cart and quantity changes
    subscribeToCartUpdate();
    
    // Trigger sync on cart page load
    triggerCartPageSync();
    
    // Setup checkout intent listener
    setupCheckoutIntentListener();
  }

  /**
   * Handles cross-tab localStorage changes.
   * Only reacts to UTM source changes (coupon-discount-utm-source) and cache keys.
   * coupon-discount-code is no longer used (cart-based approach removed in PR 2).
   */
  window.addEventListener('storage', function(event) {
    if (!event.key) return;

    var isUtmSourceKey = event.key === 'coupon-discount-utm-source';
    var isCouponCacheKey = event.key.indexOf(CACHE_KEY_PREFIX) === 0;

    if (!isUtmSourceKey && !isCouponCacheKey) return;

    // UTM source changed: re-initialize with new campaign
    if (isUtmSourceKey) {
      handleStorageUtmChange(event.newValue);
    }

    // Cache key changed: just invalidate cache, no re-initialization needed
    // (cache is re-read on next pricing check)
  });

  // ─── Boot ────────────────────────────────────────────────────

  /**
   * Deferred initialization via requestIdleCallback
   */
  document.addEventListener('DOMContentLoaded', function() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(init, { timeout: 1000 });
    } else {
      setTimeout(init, 100);
    }
  });

})();
